import { useInfiniteConnections } from 'services/repositories/connections/connections';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useInfiniteTransactions } from 'services/repositories/transactions/transactions';
import { FEATURE_FLAGS, usePartnerFeatureFlag } from 'support/helpers/featureFlags/featureFlags';
import { CompanyType, DocumentType, PartnerType, TransactionFlowState } from 'support/types';
import { Page } from 'components/Page/Page';

import { GraphsDashboard } from './GraphsDashboard/GraphsDashboard';
import { InboxDashboard } from './InboxDashboard/InboxDashboard';
import { MissingPricatDashboard } from './MissingPricatDashboard/MissingPricatDashboard';

const pricatFilterValues = [
  {
    key: 'message_type',
    value: DocumentType.productCatalog,
  },
];

const transactionsFilterValues = [
  {
    key: 'transaction_flow',
    value: TransactionFlowState.live,
  },
];

export function Dashboard() {
  const { data: currentPartner, isLoading: isLoadingCurrentPartner } = useCurrentPartner();
  const { data: liveTransactionsData, isLoading: isLoadingLiveTransactions } = useInfiniteTransactions({
    variables: {
      query: {
        filterValues: transactionsFilterValues,
        perPage: '1',
      },
    },
  });

  const { data: pricatRelationshipsData, isLoading: isLoadingPricatRelationships } = useInfiniteConnections({
    variables: {
      query: {
        filterValues: pricatFilterValues,
        perPage: '5',
      },
    },
  });

  const { enabled: isPricatEnabled } = usePartnerFeatureFlag(FEATURE_FLAGS.TEMPORARY_PRICE_CATALOGS);

  // Return loading state if needed
  if (isLoadingPricatRelationships || isLoadingLiveTransactions || isLoadingCurrentPartner) {
    return <Page isLoading={true}>Loading...</Page>;
  }

  // The MissingPricatDashboard's CTA navigates to the price-catalog routes, which are gated behind
  // TEMPORARY_PRICE_CATALOGS. Without the flag the guard bounces back to the dashboard, so only show
  // this onboarding screen when the partner can actually reach those routes.
  const shouldShowMissingPricatDashboard =
    isPricatEnabled &&
    !liveTransactionsData?.pages[0]?.count &&
    pricatRelationshipsData?.pages
      .flatMap((page) => page.data)
      .some((connection) =>
        // That means the other partner is the receiver, not the current partner
        connection.receiver_relationships?.some(
          (relationship) => relationship.message_type === DocumentType.productCatalog,
        ),
      );

  if (shouldShowMissingPricatDashboard) {
    return <MissingPricatDashboard />;
  }

  const isSupplier = currentPartner?.companyType?.includes(CompanyType.SUPPLIER);
  const isNetworkMember = currentPartner?.partnerType === PartnerType.NETWORK_MEMBER;
  const isNetworkMemberSupplier = isSupplier && isNetworkMember;

  if (isNetworkMemberSupplier) {
    return <InboxDashboard />;
  }

  return <GraphsDashboard />;
}
